import { Listing } from '@ha/api/v2/types/Listing';
import { Unit } from '@ha/api/v2/types/inventory/UnitType';
import { User } from '@ha/api/v2/types/User';
import { UserInformation } from '@ha/api/v2/types/UserInformation';
import { GlobalState } from 'ha/types/store';
import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';
import { RentalConditions } from '@ha/api/v2/types/RentalConditions';
import { Conversation } from '@ha/api/v2/types';
import { UserEntity } from 'ha/types/UserEntity';
import { AdvertiserReportsResponse } from '@ha/api/v2/types/surveyReviews/AdvertiserReports';
import { AdvertiserOverallRatingResponse } from '@ha/api/v2/types/surveyReviews/AdvertiserOverallRating';

export type RootState = GlobalState;

export interface Image {
  url: string;
  thumbnail: string;
  type: GalleryContentType;
}

export interface Video {
  vimeoId: string;
  id: number;
  youtubeId: string;
}

export type AdvertiserInformation = WithRequiredProperties<
  UserInformation,
  | 'listingsCount'
  | 'savedPaymentMethodsCount'
  | 'advertiserBookingsCount'
  | 'isKYCDone'
>;

/**
 * similar to `UserEntity`
 * @deprecated legacy from graphql era representing listing advertiser
 * @todo replace with `User` and `UserInformation` from API V2
 */
type AdvertiserEntity = User &
  AdvertiserInformation &
  Pick<
    UserEntity,
    | 'entityId'
    | 'uniID'
    | 'registeredSince'
    | 'hasPaymentMethod'
    | 'completedBookingsCount'
    | 'lastMessage'
  > & {
    isManagedAccount?: boolean;
  };

/**
 * @deprecated legacy from graphql era representing listing and used in LDP
 * @todo replace with `Listing` from API V2
 */
export type ListingEntity = WithRequiredProperties<
  Listing,
  | 'photoURLList'
  | 'exclusions'
  | 'bookablePeriods'
  | 'minimumStayMonths'
  | 'maxBookableDays'
> & {
  /**
   * @deprecated use `id`
   */
  entityId: string;
  /**
   * @deprecated
   */
  searchURL: {
    name: string;
    path: string;
  };
  advertiser: AdvertiserEntity;
  rentalConditions: RentalConditions | null;
  lastConversation: Conversation | null;
  isMultiUnit?: boolean;
  units?: Unit[];
};

export interface ListingState {
  entity: ListingEntity;
  loadState: {
    cityLocalised: string;
    countryLocalised: string;
    loading: boolean;
    hasError: boolean;
    isNotFound: boolean;
    initialized: boolean;
    isInstantBookable: boolean;
  };
  queryParams: {
    startDate: string | null;
    endDate: string | null;
  };
  similarListings: {
    listings: NormalizedAlgoliaListing[];
    loadingSimilarListings: boolean;
  };
  translation: {
    sourceLanguage: string;
    translation: string;
    isLoading: boolean;
    isShown: boolean;
    error: boolean;
  };
  fullscreenMap: {
    isFullscreenMapOpen: boolean;
  };
  tenantReviews: {
    reports: AdvertiserReportsResponse;
    overallRating: AdvertiserOverallRatingResponse;
  };
}

export enum GalleryContentType {
  Photo = 'photo',
  Floorplan = 'floorplan',
  PanoramicPhoto = '360',
  Video = 'video',
  Map = 'map',
  VirtualTour = '3D Tour',
}
